/**
 * Unified Idea Generation API Client with React Query Hooks
 *
 * Provides type-safe API client for Episode Idea Generation using:
 * - Zod for runtime validation
 * - React Query for data fetching
 * - Proper error handling and transformations
 *
 * Story: 11.16 - Unified Episode Idea Generator
 */

import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { apiPost } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas
// ============================================================================

// Idea Modifiers Schema (used for type inference)
const _IdeaModifiersSchema = z.object({
  addCreativity: z.boolean().optional(),
  addCulturalDiversity: z.boolean().optional(),
  multiPartyConversations: z.boolean().optional(),
  cultureSpecific: z.boolean().optional(),
});

// Unified Idea Candidate Schema
// Story 15.8: tagIds are now integers (migrated from UUID)
const UnifiedIdeaCandidateSchema = z.object({
  tagNames: z.array(z.string()),
  tagIds: z.array(z.number().int()),
  skillLevel: z.number(),
  skillLevelName: z.string(),
  ideaNumber: z.number(),
  candidateNumber: z.number(),
  idea: z.string(),
  duration: z.string(),
  isSelected: z.boolean(),
  selectionReason: z.string().nullable().optional(),
  learningGoalId: z.number().int().nullable().optional(),
  learningGoalName: z.string().nullable().optional(),
});

// Unified Metadata Schema
const UnifiedMetadataSchema = z.object({
  totalCandidates: z.number(),
  totalSelected: z.number(),
  combinations: z.number(),
  generationTimeSeconds: z.number(),
});

// Unified Response Schema
const UnifiedIdeaResponseSchema = z.object({
  ideas: z.array(UnifiedIdeaCandidateSchema),
  metadata: UnifiedMetadataSchema,
});

// Export TypeScript types from Zod schemas
export type IdeaModifiers = z.infer<typeof _IdeaModifiersSchema>;
export type UnifiedIdeaCandidate = z.infer<typeof UnifiedIdeaCandidateSchema>;
export type UnifiedIdeaMetadata = z.infer<typeof UnifiedMetadataSchema>;
export type UnifiedIdeaResponse = z.infer<typeof UnifiedIdeaResponseSchema>;

// Request interface
// Story 15.8: tags are now integer IDs (migrated from UUID)
export interface UnifiedIdeaGenerationRequest {
  tags?: number[]; // Story 15.8: Integer tag IDs (optional if learningGoals provided)
  learningGoals?: number[]; // Learning goal IDs (optional if tags provided)
  skillLevels: number[]; // [2, 3, 4, 5]
  ideasPerCombination?: number; // 1-5, default 1
  candidatesPerIdea?: number; // 1-10, default 5
  seedText?: string;
  durations?: string[]; // Short, Medium, Long, Extended (empty = any)
  modifiers?: IdeaModifiers;
}

// ============================================================================
// STEP 2: Define Fetcher Functions
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase request to snake_case for API
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

/**
 * Generate ideas using unified endpoint
 */
async function generateIdeas(request: UnifiedIdeaGenerationRequest): Promise<UnifiedIdeaResponse> {
  logger.debug('Generating ideas with request:', request);

  // Transform request to snake_case for API
  const apiRequest = transformCamelToSnake(request as unknown as Record<string, unknown>);

  // Use 10 minute timeout for large generation jobs
  const response = await apiPost<Record<string, unknown>>(
    '/api/admin/generation/ideas',
    apiRequest,
    600000 // 10 minutes
  );

  // Transform snake_case response to camelCase
  const transformedResponse = transformSnakeToCamel<UnifiedIdeaResponse>(response);

  // Validate response against schema
  const validated = UnifiedIdeaResponseSchema.parse(transformedResponse);

  logger.debug(
    `Successfully generated ${validated.metadata.totalCandidates} candidates, ` +
      `${validated.metadata.totalSelected} selected`
  );

  return validated;
}

// ============================================================================
// STEP 3: Define React Query Hooks
// ============================================================================

/**
 * Hook to generate ideas using unified endpoint
 *
 * Usage:
 * ```tsx
 * const { mutate: generate, data, isPending, error } = useGenerateIdeas();
 *
 * // Trigger generation
 * generate({
 *   tags: ['uuid-1', 'uuid-2'],
 *   skillLevels: [2, 3],
 *   ideasPerCombination: 1,
 *   candidatesPerIdea: 5,
 *   duration: 'any',
 *   modifiers: { addCreativity: true },
 * });
 * ```
 */
export function useGenerateIdeas() {
  return useMutation({
    mutationFn: generateIdeas,
    retry: 0, // Don't retry on failure (long operation)
  });
}

// ============================================================================
// CSV Generation Utilities
// ============================================================================

/**
 * Escape CSV field value
 */
function escapeCSV(value: string | number | boolean | null | undefined): string {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generate CSV from idea candidates
 *
 * CSV includes ALL candidates (not just selected) for offline review
 */
export function generateCSV(ideas: UnifiedIdeaCandidate[], languageCode: string = 'en'): string {
  const headers = [
    'tag_names',
    'tag_ids',
    'skill_level',
    'skill_level_name',
    'idea_number',
    'candidate_number',
    'idea',
    'duration',
    'is_selected',
    'selection_reason',
    'learning_goal_id',
    'learning_goal_name',
    'language_code',
    'generated_at',
  ];

  const timestamp = new Date().toISOString();

  const rows = ideas.map((idea) => [
    escapeCSV(idea.tagNames.join(';')),
    escapeCSV(idea.tagIds.join(';')),
    escapeCSV(idea.skillLevel),
    escapeCSV(idea.skillLevelName),
    escapeCSV(idea.ideaNumber),
    escapeCSV(idea.candidateNumber),
    escapeCSV(idea.idea),
    escapeCSV(idea.duration),
    escapeCSV(idea.isSelected),
    escapeCSV(idea.selectionReason ?? ''),
    escapeCSV(idea.learningGoalId ?? ''),
    escapeCSV(idea.learningGoalName ?? ''),
    escapeCSV(languageCode),
    escapeCSV(timestamp),
  ]);

  return [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
}

/**
 * Download CSV file
 */
export function downloadCSV(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ============================================================================
// Backward Compatibility (DEPRECATED - Remove after Story 11.16 complete)
// ============================================================================

// Re-export legacy types for backward compatibility during migration
export type GeneratedIdea = {
  idea: string;
  suggestedComplexity: string;
  suggestedDuration: string;
};
